import {createSelector} from '@ngrx/store';
import {
  canUserAccessCourse,
  isAdmin,
  isCourseAdmin,
  isLoggedOut,
  selectActiveCourse,
  selectActiveCourseAllLessons,
  selectActiveCourseSections,
  selectActiveCourseTotalArticles,
  selectAllowedCourses,
  selectTenantInfo,
  selectTranslatedLanguageNamesMap,
  selectUser,
  selectUserPermissions
} from '../store/selectors';
import {selectActiveCourseLessonsWatched} from '../store/user-lesson-status.selectors';
import {selectActiveCourseAuthors} from '../store/author.selectors';
import {compareSections, sortLessonsBySectionAndSeqNo} from '../common/sort-model';
import {Tenant} from '../models/tenant.model';
import {calculateSectionLessons} from '../models/course-section.model';
import {CoursePageData} from './course-page-data.model';


export const selectCourseData = createSelector(
  selectActiveCourse,
  selectActiveCourseSections,
  selectActiveCourseAllLessons,
  selectActiveCourseLessonsWatched,
  selectActiveCourseAuthors,
  selectActiveCourseTotalArticles,
  (course, sections, lessons, lessonsWatched, courseAuthors, totalArticles) => {
    const sortedSections = sections?.sort(compareSections)
    return {
      course, sections:sortedSections, lessons, lessonsWatched, courseAuthors, totalArticles
    }
  }
);

export const selectUserPermissionsData = createSelector(
  isLoggedOut,
  canUserAccessCourse,
  selectUserPermissions,
  isAdmin,
  isCourseAdmin,
  selectAllowedCourses,
  selectUser,
  (isLoggedOut, canUserAccessCourse, permissions, isAdmin, isCourseAdmin, allowedCourses, user) => {
    return {
      isLoggedOut, canUserAccessCourse, permissions, isAdmin, isCourseAdmin, allowedCourses, user
    }
  }
);

export const coursePageDataSelector = createSelector(
  selectCourseData,
  selectUserPermissionsData,
  selectTenantInfo,
  selectTranslatedLanguageNamesMap,
  (courseData, userPermissionsData, tenant, translatedLanguageNamesMap) => {

    const publishedLessons = courseData.lessons
      .filter((lesson) => lesson.status == "published")
      .map((lesson) => lesson.captions)
      .filter((lesson) => !!lesson);

    const captionsSet = new Set<string>();

    for (const captions of publishedLessons) {
      for (const caption of captions) {
        captionsSet.add(caption.language);
      }
    }

    // old code
    // const uniqueCaptions = Array.from(captionsSet).map(
    //   (languageCode) =>
    //     translatedLanguageNamesMap[languageCode.toLowerCase()]
    // );

    // new code
    const uniqueCaptions = Array.from(captionsSet).map(
      (languageCode) => {
        if (languageCode === "ZH") {
          return translatedLanguageNamesMap["zh-CN"];
        } else {
          return translatedLanguageNamesMap[languageCode.toLowerCase()];
        }
    });

    const data: CoursePageData = {
      ...courseData,
      ...userPermissionsData,
      tenant: tenant as Tenant,
      captionsList: uniqueCaptions.join(", "),
    }

    data.sectionsLessons = {};

    if (courseData.sections?.length && courseData.lessons?.length) {
      for (let section of courseData.sections) {
        data.sectionsLessons[section.id] = calculateSectionLessons(
          section,
          courseData.lessons
        );
      }
    }

    if (courseData.sections && courseData.lessons) {
      data.lessons = sortLessonsBySectionAndSeqNo(courseData.lessons, courseData.sections);
    }

    return data;
  }
);

